import { Banner, Button, Checkbox } from "components/ui";
import { usePeriodStrings } from "translation/periodManagement";
import { matchPreset } from "./takeoverConfig";

/**
 * Takeover selection for a new period, for either module.
 *
 * Two presets act as templates, everything below them is freely selectable, and
 * any deviation from a preset marks the takeover as custom. Everything that
 * differs between LOB and DB comes in through `config`, see takeoverConfig.js.
 *
 * `areaExtras` lets the caller hang a module specific control under one area,
 * shown only while that area is selected. LOB uses it for the three way
 * Zielvereinbarungen choice under Leistungskarten.
 */

const toggle = (list, item) =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

export default function PeriodTakeover({ config, value, onChange, sourceName, areaExtras = {} }) {
  const { pt, ps } = usePeriodStrings();

  const preset = matchPreset(config, value);
  const fieldsOn = value.areas.includes(config.fieldsArea);
  const fieldKeys = config.fields.map((f) => f.key);
  const set = (patch) => onChange({ ...value, ...patch });

  const toggleArea = (key) => set({ areas: toggle(value.areas, key) });

  return (
    <>
      <span className="peb-period-form__section-label">{pt("period_management.takeover.title")}</span>
      <p className="peb-takeover__intro">{ps(config.introKey, { name: sourceName })}</p>

      <div className="peb-takeover__presets">
        {Object.keys(config.presets).map((name) => (
          <button
            type="button"
            key={name}
            data-testid={`takeover-preset-${name}`}
            aria-pressed={preset === name}
            className={`peb-takeover__preset ${preset === name ? "is-selected" : ""}`}
            onClick={() => set(config.presets[name])}
          >
            {pt(`period_management.copy_mode.${name}`)}
          </button>
        ))}
        {preset === null && (
          <span className="peb-takeover__custom-tag" data-testid="takeover-custom-tag">
            {pt("period_management.takeover.custom")}
          </span>
        )}
      </div>
      <p className="peb-takeover__preset-desc" data-testid="takeover-preset-desc">
        {pt(config.presetDescKeys[preset || "custom"])}
      </p>

      <span className="peb-period-form__section-label">{pt("period_management.takeover.areas_title")}</span>
      <div className="peb-takeover__areas">
        {config.fixedAreas.map((area) => (
          <label key={area.key} className="peb-takeover__area is-fixed">
            <input
              type="checkbox"
              className="peb-checkbox__input"
              data-testid={`takeover-area-${area.key}`}
              checked
              disabled
              readOnly
            />
            <span className="peb-takeover__area-body">
              <span className="peb-takeover__area-title">
                {pt(area.labelKey)}
                <span className="peb-takeover__area-fixed-note">
                  {pt("period_management.takeover.area.always_note")}
                </span>
              </span>
              <span className="peb-takeover__area-desc">{pt(area.descKey)}</span>
            </span>
          </label>
        ))}

        {config.areas.map((area) => {
          const selected = value.areas.includes(area.key);
          const extra = areaExtras[area.key];

          return (
            <div key={area.key}>
              <label
                className={[
                  "peb-takeover__area",
                  selected ? "is-selected" : "",
                ].filter(Boolean).join(" ")}
              >
                <input
                  type="checkbox"
                  className="peb-checkbox__input"
                  data-testid={`takeover-area-${area.key}`}
                  checked={selected}
                  onChange={() => toggleArea(area.key)}
                />
                <span className="peb-takeover__area-body">
                  <span className="peb-takeover__area-title">
                    {pt(area.labelKey)}
                  </span>
                  <span className="peb-takeover__area-desc">{pt(area.descKey)}</span>
                </span>
              </label>
              {selected && area.implicitItems?.map((item) => (
                <label
                  key={item.key}
                  className="peb-takeover__area is-implicit peb-takeover__area--implicit"
                  data-testid={`takeover-implicit-${item.key}`}
                >
                  <input
                    type="checkbox"
                    className="peb-checkbox__input"
                    checked
                    disabled
                    readOnly
                    data-testid={`takeover-implicit-${item.key}-input`}
                  />
                  <span className="peb-takeover__area-body">
                    <span className="peb-takeover__area-title">
                      {pt(item.labelKey)}
                      <span className="peb-takeover__area-fixed-note">{pt(item.noteKey)}</span>
                    </span>
                    <span className="peb-takeover__area-desc">{pt(item.descKey)}</span>
                  </span>
                </label>
              ))}
              {extra && selected && <div className="peb-takeover__area-extra">{extra}</div>}
            </div>
          );
        })}
      </div>

      <div className={`peb-takeover__fields ${fieldsOn ? "" : "is-disabled"}`}>
        <div className="peb-takeover__fields-head">
          <span className="peb-takeover__fields-title">{pt("period_management.takeover.fields_title")}</span>
          <span className="peb-takeover__fields-count" data-testid="takeover-field-count">
            {fieldsOn
              ? ps("period_management.takeover.fields_count", {
                  count: value.fields.length,
                  total: config.fields.length,
                })
              : ps("period_management.takeover.fields_no_employees")}
          </span>
          <span className="peb-takeover__bulk">
            <Button
              variant="secondary"
              size="sm"
              data-testid="takeover-bulk-all"
              onClick={() => set({ fields: [...fieldKeys] })}
            >
              {pt("period_management.takeover.bulk_all")}
            </Button>
            <Button variant="secondary" size="sm" data-testid="takeover-bulk-none" onClick={() => set({ fields: [] })}>
              {pt("period_management.takeover.bulk_none")}
            </Button>
          </span>
        </div>

        <div className="peb-takeover__field-grid">
          {config.fields.map((f) => (
            <Checkbox
              key={f.key}
              className="peb-takeover__field"
              data-testid={`takeover-field-${f.key}`}
              checked={value.fields.includes(f.key)}
              onChange={() => set({ fields: toggle(value.fields, f.key) })}
              label={pt(`period_management.takeover.field.${f.key}`)}
            />
          ))}
        </div>

        <p className="peb-takeover__field-note" data-testid="takeover-field-note">
          {pt("period_management.takeover.fields_free_note")}
        </p>

        <div className="peb-takeover__notes">
          <div className="peb-takeover__note">
            <strong>{pt("period_management.takeover.always_title")}</strong>
            {pt(config.alwaysBodyKey)}
          </div>
          <div className="peb-takeover__note">
            <strong>{pt("period_management.takeover.never_title")}</strong>
            {pt(config.neverBodyKey)}
          </div>
        </div>
      </div>

      {config.previousResult && (
        <>
          <span className="peb-period-form__section-label">
            {pt("period_management.takeover.previous_result_title")}
          </span>
          <Banner
            intent="info"
            className="peb-takeover__lookup"
            title={pt("period_management.takeover.previous_result_head")}
          >
            <p className="mb-0">{pt("period_management.takeover.previous_result_body")}</p>
            <p className="peb-takeover__source mb-0">
              {ps("period_management.takeover.previous_result_source", { name: sourceName })}
            </p>
          </Banner>
        </>
      )}

      <span className="peb-period-form__section-label">
        {pt("period_management.takeover.visibility_title")}
        {!fieldsOn && (
          <span className="peb-takeover__section-note" data-testid="takeover-visibility-note">
            {pt("period_management.takeover.visibility_no_employees")}
          </span>
        )}
      </span>
      {/* Without employees the choice has nothing to act on, so it is dimmed
          the same way the field grid above it is rather than sitting there
          looking like a decision that matters. */}
      <div className={`peb-takeover__areas ${fieldsOn ? "" : "is-disabled"}`}>
        {[true, false].map((visible) => {
          const key = visible ? "visible" : "hidden";

          return (
            <label
              key={key}
              className={`peb-takeover__area ${value.visible === visible ? "is-selected" : ""}`}
            >
              <input
                type="radio"
                name="takeover-visibility"
                className="peb-checkbox__input"
                data-testid={`takeover-visibility-${key}`}
                checked={value.visible === visible}
                onChange={() => set({ visible })}
              />
              <span className="peb-takeover__area-body">
                <span className="peb-takeover__area-title">
                  {pt(`period_management.takeover.visibility.${key}`)}
                </span>
                <span className="peb-takeover__area-desc">
                  {pt(`period_management.takeover.visibility.${key}.desc`)}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <p className="peb-takeover__irreversible" data-testid="takeover-irreversible">
        {pt("period_management.takeover.irreversible")}
      </p>
    </>
  );
}
